import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { mockSalesOrders, type MockSalesOrder } from '@/mocks/billing';
import { mockParties } from '@/mocks/masters';
import { formatINR } from '@/utils/format';
import { salesOrderService } from '@/services/salesOrderService';
import { getAllWarehouses, type WarehouseResponse } from '@/api/warehouse.api';
import { useWarehouseStore } from '@/stores/warehouseStore';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
  CONFIRMED: 'bg-blue-50 text-blue-700 border-blue-200',
  PARTIAL: 'bg-amber-50 text-amber-700 border-amber-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
};

const today = new Date().toISOString().split('T')[0];

function daysBetween(a: string, b: string) {
  return Math.floor((new Date(a).getTime() - new Date(b).getTime()) / 86400000);
}

export default function SalesOrdersPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId: topWarehouseId } = useWarehouseStore();
  const [salesOrders, setSalesOrders] = useState<MockSalesOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'all' | 'dashboard'>('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(topWarehouseId || 'ALL');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editNumberItem, setEditNumberItem] = useState<{ id: string; number: string; moduleKey: string } | null>(null);

  // Sync with global topbar warehouse selector
  useEffect(() => {
    if (topWarehouseId) {
      setSelectedWarehouseId(topWarehouseId);
    }
  }, [topWarehouseId]);

  // Load warehouses
  useEffect(() => {
    async function loadWarehouses() {
      try {
        const res = await getAllWarehouses();
        if (res && res.data && Array.isArray(res.data)) {
          setWarehouses(res.data.map((w: WarehouseResponse) => ({ id: w.id, name: w.name })));
        }
      } catch (e) {
        console.warn('Failed to load warehouses:', e);
      }
    }
    loadWarehouses();
  }, []);

  // Fetch sales orders from backend API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await salesOrderService.listSalesOrders({
        search: search || undefined,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        warehouseId: selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setSalesOrders(data || []);
    } catch (err: any) {
      console.warn('Failed to load from API:', err);
      setSalesOrders([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, selectedWarehouseId, dateFrom, dateTo]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filtered = useMemo(() => {
    let list = [...salesOrders];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (o) =>
          o.soNumber.toLowerCase().includes(q) ||
          o.customerName.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'All') {
      list = list.filter((o) => o.status === statusFilter.toUpperCase());
    }
    if (dateFrom) list = list.filter((o) => o.date >= dateFrom);
    if (dateTo) list = list.filter((o) => o.date <= dateTo);
    return list;
  }, [salesOrders, search, statusFilter, dateFrom, dateTo]);

  const openCount = useMemo(() => salesOrders.filter((o) => o.status === 'CONFIRMED' || o.status === 'PARTIAL').length, [salesOrders]);
  const partialCount = useMemo(() => salesOrders.filter((o) => o.status === 'PARTIAL').length, [salesOrders]);
  const pendingValue = useMemo(() => salesOrders
    .filter((o) => o.status === 'CONFIRMED' || o.status === 'PARTIAL')
    .reduce((s, o) => s + (o.pendingAmount || 0), 0), [salesOrders]);

  const completedThisMonth = useMemo(() => salesOrders.filter((o) => {
    if (o.status !== 'COMPLETED') return false;
    const d = new Date(o.date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length, [salesOrders]);

  const getCustomerAddress = (customerId: string) => {
    const p = mockParties.find((x) => x.id === customerId);
    return p?.billingAddress ?? '';
  };

  const isOverdue = (so: MockSalesOrder) => {
    if (so.status === 'COMPLETED' || so.status === 'CANCELLED') return false;
    if (!so.expectedDeliveryDate) return false;
    return so.expectedDeliveryDate < today;
  };

  const fulfillmentPct = (so: MockSalesOrder) => {
    const totalOrdered = (so.items || []).reduce((s, i) => s + i.orderedQty, 0);
    const totalDelivered = (so.items || []).reduce((s, i) => s + i.deliveredQty, 0);
    if (totalOrdered === 0) return 0;
    return Math.round((totalDelivered / totalOrdered) * 100);
  };

  const handleCreateInvoice = (so: MockSalesOrder) => {
    navigate('/sales/invoices/new', {
      state: {
        fromSalesOrder: {
          soId: so.id,
          soNumber: so.soNumber,
          customerId: so.customerId,
          customerName: so.customerName,
          customerGSTIN: so.customerGSTIN,
          items: (so.items || []).filter((i) => (i.pendingQty ?? i.orderedQty ?? 0) > 0).map((i) => ({
            soItemId: (i as any).id || (i as any).soItemId,
            itemId: i.itemId,
            itemName: i.itemName,
            hsnCode: i.hsnCode,
            qty: (i.pendingQty !== undefined && i.pendingQty > 0) ? i.pendingQty : i.orderedQty,
            unit: i.unit,
            rate: i.rate,
            discount: i.discount,
            taxRate: i.taxRate,
          })),
        },
      },
    });
  };

  const handleCreateChallan = (so: MockSalesOrder) => {
    navigate('/sales/challans', {
      state: {
        fromSalesOrder: {
          soId: so.id,
          soNumber: so.soNumber,
          customerId: so.customerId,
          customerName: so.customerName,
          warehouseId: (so as any).warehouseId,
          items: (so.items || []).filter((i) => (i.pendingQty ?? i.orderedQty ?? 0) > 0).map((i) => ({
            itemId: i.itemId,
            itemCode: i.itemCode,
            itemName: i.itemName,
            hsnCode: i.hsnCode,
            orderedQty: i.orderedQty,
            deliveredQty: i.deliveredQty,
            pendingQty: i.pendingQty ?? i.orderedQty,
            qty: (i.pendingQty && i.pendingQty > 0) ? i.pendingQty : (i.orderedQty || 1),
            unit: i.unit,
            unitId: i.unitId,
            rate: i.rate,
            discount: i.discount,
            taxRate: i.taxRate,
            taxableAmount: i.taxableAmount,
            total: i.total,
          })),
        },
      },
    });
  };

  // ─── Dashboard data ───
  const statusCounts = useMemo(() => {
    return {
      confirmed: salesOrders.filter((o) => o.status === 'CONFIRMED').length,
      partial: salesOrders.filter((o) => o.status === 'PARTIAL').length,
      completed: salesOrders.filter((o) => o.status === 'COMPLETED').length,
      cancelled: salesOrders.filter((o) => o.status === 'CANCELLED').length,
    };
  }, [salesOrders]);
  const maxStatusCount = Math.max(statusCounts.confirmed, statusCounts.partial, statusCounts.completed, statusCounts.cancelled, 1);

  const last5Completed = useMemo(() => {
    return salesOrders
      .filter((o) => o.status === 'COMPLETED')
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 5);
  }, [salesOrders]);

  const topPendingCustomers = useMemo(() => {
    const map = new Map<string, { customerName: string; openSOs: number; pendingValue: number; oldestDate: string }>();
    salesOrders.filter((o) => o.status === 'CONFIRMED' || o.status === 'PARTIAL').forEach((so) => {
      const existing = map.get(so.customerId);
      if (existing) {
        existing.openSOs += 1;
        existing.pendingValue += so.pendingAmount;
        if (so.date < existing.oldestDate) existing.oldestDate = so.date;
      } else {
        map.set(so.customerId, { customerName: so.customerName, openSOs: 1, pendingValue: so.pendingAmount, oldestDate: so.date });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.pendingValue - a.pendingValue).slice(0, 5);
  }, [salesOrders]);

  const overdueAlerts = useMemo(() => {
    return salesOrders.filter((o) => {
      if (o.status === 'COMPLETED' || o.status === 'CANCELLED') return false;
      if (!o.expectedDeliveryDate) return false;
      return o.expectedDeliveryDate < today;
    });
  }, [salesOrders]);

  const fl = 'w-full h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700';

  const handleCancelSO = async (so: MockSalesOrder) => {
    const pendingItems = (so.items || []).filter((i) => i.pendingQty > 0);
    const pendingCount = pendingItems.reduce((s, i) => s + i.pendingQty, 0);
    if (!window.confirm(`Cancel ${so.soNumber}? ${pendingCount} items not delivered.`)) return;
    try {
      await salesOrderService.updateStatus(so.id, 'CANCELLED');
      setSalesOrders((prev) =>
        prev.map((item) => (item.id === so.id ? { ...item, status: 'CANCELLED' as const } : item))
      );
    } catch (err) {
      console.error('Failed to cancel order:', err);
      // Optimistic update fallback
      setSalesOrders((prev) =>
        prev.map((item) => (item.id === so.id ? { ...item, status: 'CANCELLED' as const } : item))
      );
    }
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Sales Orders</h1>
            <p className="text-sm text-slate-500">Order management and fulfillment tracking</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/sales/orders/report')} className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-bar-chart-line" /> Order Book
            </button>
            <button
              onClick={() => navigate('/sales/orders/new')}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-add-line" /> New Sales Order
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-5">
          {(['all', 'dashboard'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`h-9 px-4 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                activeTab === t
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t === 'all' ? 'All Orders' : 'Dashboard'}
            </button>
          ))}
        </div>

        {activeTab === 'all' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Open Orders</p>
                <p className="text-2xl font-bold text-blue-600">{openCount}</p>
                <p className="text-xs text-slate-400 mt-0.5">Pending fulfillment</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Partial Orders</p>
                <p className="text-2xl font-bold text-amber-600">{partialCount}</p>
                <p className="text-xs text-slate-400 mt-0.5">Partially delivered</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Pending Value</p>
                <p className="text-2xl font-bold text-red-600">{formatINR(pendingValue)}</p>
                <p className="text-xs text-slate-400 mt-0.5">Across open orders</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Completed (This Month)</p>
                <p className="text-2xl font-bold text-emerald-600">{completedThisMonth}</p>
                <p className="text-xs text-slate-400 mt-0.5">Fully delivered</p>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by SO number or customer..."
                    className="w-full h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700"
                  />
                </div>
                <select
                  value={selectedWarehouseId}
                  onChange={(e) => setSelectedWarehouseId(e.target.value)}
                  className="h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700 w-44 cursor-pointer"
                >
                  <option value="ALL">All Warehouses</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700 w-36 cursor-pointer"
                >
                  <option value="All">All Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Partial">Partial</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-slate-400">From:</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 px-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700 w-36"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-slate-400">To:</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 px-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700 w-36"
                  />
                </div>
                <button
                  onClick={() => { setSearch(''); setStatusFilter('All'); setSelectedWarehouseId('ALL'); setDateFrom(''); setDateTo(''); }}
                  className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto min-h-[300px]">
              {loading ? (
                <div className="p-8 text-center text-slate-500">Loading sales orders from server...</div>
              ) : (
                <table className="w-full min-w-[1100px] text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      {['SO Number', 'Customer', 'Date', 'Expected Delivery', 'Items', 'Order Value', 'Delivered', 'Pending', 'Status', 'Actions'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-slate-200 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((so, idx) => {
                      const isOver = isOverdue(so);
                      const pct = fulfillmentPct(so);
                      const itemsList = so.items || [];
                      const canCancel = (so.status === 'DRAFT' || so.status === 'CONFIRMED') && itemsList.every((i) => i.deliveredQty === 0);
                      return (
                        <tr key={so.id} className={`border-b border-slate-100 hover:bg-indigo-50/30 transition-colors ${idx % 2 === 1 ? 'bg-slate-50/60' : ''}`}>
                                  <td className="px-4 py-3 font-medium text-indigo-600 whitespace-nowrap">
                            <span className="cursor-pointer hover:underline" onClick={() => navigate(`/sales/orders/${so.id}`)}>
                              {so.soNumber}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-800">{so.customerName}</p>
                            <p className="text-xs text-slate-400">{getCustomerAddress(so.customerId)}</p>
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{so.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-slate-600">{so.expectedDeliveryDate || '—'}</span>
                            {isOver && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-medium">Overdue</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-slate-600">{itemsList.length}</td>
                          <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{formatINR(so.totalAmount)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-emerald-600 font-medium">{formatINR(so.deliveredAmount)}</span>
                            {so.deliveredAmount > 0 && <span className="text-xs text-slate-400 ml-1">({pct}%)</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={so.pendingAmount > 0 ? 'text-amber-600 font-medium' : 'text-slate-400'}>
                              {formatINR(so.pendingAmount)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium w-fit ${STATUS_STYLES[so.status] || STATUS_STYLES.DRAFT}`}>
                                {so.status}
                              </span>
                              {/* Challan number commented out
                              {(so.challanNumber || (so.linkedChallans && so.linkedChallans.length > 0)) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const num = so.challanNumber || so.linkedChallans?.[0]?.challanNumber;
                                    navigate('/sales/challans', { state: { searchChallanNo: num, openChallanNo: num } });
                                  }}
                                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium hover:bg-emerald-100 transition-colors cursor-pointer w-fit"
                                  title="Click to view delivery challan"
                                >
                                  <i className="ri-truck-line text-emerald-600" />
                                  {so.challanNumber || so.linkedChallans?.[0]?.challanNumber}
                                </button>
                              )}
                              */}
                              {so.status === 'PARTIAL' && (
                                <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-amber-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              )}
                              {so.status === 'PARTIAL' && (
                                <span className="text-[10px] text-slate-400">
                                  {itemsList.reduce((s, i) => s + i.deliveredQty, 0)}/{itemsList.reduce((s, i) => s + i.orderedQty, 0)} items ({pct}%)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => navigate(`/sales/orders/${so.id}`)}
                                className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap font-medium transition-colors"
                              >
                                View
                              </button>
                              <button
                                onClick={() => navigate('/sales/orders/new', { state: { editSO: so, soId: so.id } })}
                                className="text-xs px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 cursor-pointer whitespace-nowrap font-medium transition-colors flex items-center gap-1"
                                title="Edit Sales Order"
                              >
                                <i className="ri-edit-line text-xs text-indigo-600" /> Edit
                              </button>
                              {/* Challan view / creation button commented out
                              {(so.challanNumber || (so.linkedChallans && so.linkedChallans.length > 0)) ? (
                                <button
                                  onClick={() => {
                                    const num = so.challanNumber || so.linkedChallans?.[0]?.challanNumber;
                                    navigate('/sales/challans', { state: { searchChallanNo: num, openChallanNo: num } });
                                  }}
                                  className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer whitespace-nowrap font-medium transition-colors flex items-center gap-1"
                                >
                                  <i className="ri-truck-line text-emerald-600 text-xs" />
                                  {so.challanNumber || so.linkedChallans?.[0]?.challanNumber}
                                </button>
                              ) : ((so.status === 'CONFIRMED' || so.status === 'PARTIAL') && itemsList.some((i) => i.pendingQty > 0) && (
                                <button
                                  onClick={() => handleCreateChallan(so)}
                                  className="text-xs px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 cursor-pointer whitespace-nowrap font-medium transition-colors"
                                >
                                  Challan
                                </button>
                              ))}
                              */}
                              {(so.status === 'CONFIRMED' || so.status === 'PARTIAL') && itemsList.some((i) => i.pendingQty > 0) && (
                                <button
                                  onClick={() => handleCreateInvoice(so)}
                                  className="text-xs px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer whitespace-nowrap font-medium transition-colors"
                                >
                                  Invoice
                                </button>
                              )}
                              {canCancel && (
                                <button onClick={() => handleCancelSO(so)} className="text-xs px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap font-medium transition-colors">
                                  Cancel
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">No sales orders found</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {activeTab === 'dashboard' && (
          <>
            {/* Section 1: Status Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Status Breakdown</h2>
                <div className="space-y-3">
                  {[
                    { label: 'Confirmed', count: statusCounts.confirmed, color: 'bg-blue-500', text: 'text-blue-700' },
                    { label: 'Partial', count: statusCounts.partial, color: 'bg-amber-500', text: 'text-amber-700' },
                    { label: 'Completed', count: statusCounts.completed, color: 'bg-emerald-500', text: 'text-emerald-700' },
                    { label: 'Cancelled', count: statusCounts.cancelled, color: 'bg-red-400', text: 'text-red-600' },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center gap-3">
                      <span className={`text-xs font-medium w-20 ${s.text}`}>{s.label}</span>
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${s.color} rounded-full transition-all`} style={{ width: `${maxStatusCount > 0 ? (s.count / maxStatusCount) * 100 : 0}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-slate-700 w-8 text-right">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 2: Fulfillment Trend */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Last Completed Orders</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-400">
                        <th className="pb-2 font-medium">SO</th>
                        <th className="pb-2 font-medium">Customer</th>
                        <th className="pb-2 font-medium text-right">Ordered</th>
                        <th className="pb-2 font-medium text-right">Fulfilled</th>
                        <th className="pb-2 font-medium text-right">Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {last5Completed.map((so) => {
                        const itemsList = so.items || [];
                        const daysToComplete = so.date && itemsList[0] ? daysBetween(today, so.date) : 0;
                        return (
                          <tr key={so.id} className="border-b border-slate-50">
                            <td className="py-2 font-medium text-indigo-600 cursor-pointer" onClick={() => navigate(`/sales/orders/${so.id}`)}>{so.soNumber}</td>
                            <td className="py-2 text-slate-600">{so.customerName}</td>
                            <td className="py-2 text-right font-medium text-slate-700">{formatINR(so.totalAmount)}</td>
                            <td className="py-2 text-right text-emerald-600 font-medium">{formatINR(so.deliveredAmount)}</td>
                            <td className="py-2 text-right text-slate-500">{Math.abs(daysToComplete) || '—'}</td>
                          </tr>
                        );
                      })}
                      {last5Completed.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-slate-400">No completed orders yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Section 3: Top Pending Customers */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Top Pending Customers</h2>
              {topPendingCustomers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-400">
                        <th className="pb-2 font-medium">Customer</th>
                        <th className="pb-2 font-medium text-center">Open SOs</th>
                        <th className="pb-2 font-medium text-right">Pending Value</th>
                        <th className="pb-2 font-medium">Oldest Order</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPendingCustomers.map((c) => (
                        <tr key={c.customerName} className="border-b border-slate-50">
                          <td className="py-2 font-medium text-slate-800">{c.customerName}</td>
                          <td className="py-2 text-center text-slate-600">{c.openSOs}</td>
                          <td className="py-2 text-right font-semibold text-amber-600">{formatINR(c.pendingValue)}</td>
                          <td className="py-2 text-slate-500">{c.oldestDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400 py-4 text-center">No pending customers</p>
              )}
            </div>

            {/* Section 4: Overdue Alerts */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Overdue Alerts</h2>
              {overdueAlerts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-400">
                      <th className="pb-2 font-medium">SO Number</th>
                      <th className="pb-2 font-medium">Customer</th>
                      <th className="pb-2 font-medium text-center">Days Overdue</th>
                      <th className="pb-2 font-medium text-right">Pending Value</th>
                      <th className="pb-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdueAlerts.map((so) => {
                      const overdueDays = so.expectedDeliveryDate ? daysBetween(today, so.expectedDeliveryDate) : 0;
                      const itemsList = so.items || [];
                      return (
                        <tr key={so.id} className="border-b border-slate-50 bg-red-50/40">
                          <td className="py-2 font-medium text-indigo-600 cursor-pointer" onClick={() => navigate(`/sales/orders/${so.id}`)}>{so.soNumber}</td>
                          <td className="py-2 text-slate-800">{so.customerName}</td>
                          <td className="py-2 text-center">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{overdueDays} days</span>
                          </td>
                          <td className="py-2 text-right font-semibold text-red-600">{formatINR(so.pendingAmount)}</td>
                          <td className="py-2">
                            {/* Create Challan commented out
                            {itemsList.some((i) => i.pendingQty > 0) && (
                              <button onClick={() => handleCreateChallan(so)} className="text-xs px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 cursor-pointer whitespace-nowrap font-medium transition-colors">
                                Create Challan
                              </button>
                            )}
                            */}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              ) : (
                <p className="text-sm text-slate-400 py-4 text-center">No overdue orders — great job!</p>
              )}
            </div>
          </>
        )}
      </div>

    </AppLayout>
  );
}